import { SettingsToggle } from './SettingsToggle';
import { settingRowStyle as rowStyle, settingLabelColumnStyle as labelColumnStyle, settingLabelStyle as labelStyle, settingsDescriptionStyle as descriptionStyle, settingOrgIndicatorStyle as orgIndicatorStyle } from './settingsStyles';
// ----- Phase 5.X-settings-schema-ui — Toggle row -----
//
// Semantic on/off control for a single schema entry. Renders the
// label, optional org-lock indicator, the description subtitle, and a
// checkbox-style toggle. When `disabled`, the toggle is uneditable
// and a tooltip explains why (the disabled reason).

import type { CSSProperties, ReactElement } from 'react';
import type { SettingsSchemaEntry } from '@/lib/settings-schema';

const toggleColumnStyle: CSSProperties = {
  flex: '0 0 auto',
  display: 'flex',
  alignItems: 'center',
};

export interface SettingToggleRowProps {
  readonly entry: SettingsSchemaEntry;
  readonly value: boolean;
  /** True when the schema declares this an org-level key (renders 🔒). */
  readonly showOrgLock: boolean;
  /** True when the user lacks write permission for this scope. */
  readonly disabled: boolean;
  /** Reason shown as a title= tooltip when disabled. */
  readonly disabledReason?: string;
  /** Called with the new value when the user toggles. No-op when disabled. */
  readonly onChange: (next: boolean) => void;
}

export function SettingToggleRow({
  entry,
  value,
  showOrgLock,
  disabled,
  disabledReason,
  onChange,
}: SettingToggleRowProps): ReactElement {
  return (
    <div
      style={rowStyle}
      data-testid={`setting-row-${entry.key}`}
      data-setting-level={entry.level}
    >
      <div style={labelColumnStyle}>
        <div style={labelStyle}>
          {showOrgLock && (
            <span aria-label="Organization compliance control" title="Organization compliance control">
              🔒
            </span>
          )}
          <span>{entry.label}</span>
        </div>
        <div style={descriptionStyle}>{entry.description}</div>
        {showOrgLock && (
          <div style={orgIndicatorStyle}>Organization compliance control</div>
        )}
      </div>
      <div style={toggleColumnStyle}>
        <SettingsToggle
          label={entry.label}
          testId={`setting-toggle-${entry.key}`}
          checked={value}
          disabled={disabled}
          disabledReason={disabledReason}
          onChange={onChange}
        />
      </div>
    </div>
  );
}
